import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { resolveMediaUrl } from "../utils/mediaUrl";
import Pagination from "../components/Pagination";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export default function BlogPage() {
    const { isAuthenticated } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [articles, setArticles] = useState([]);
    const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const page = Math.max(1, Number(searchParams.get("page") || 1));
    const query = searchParams.get("q") || "";
    const activeTag = searchParams.get("tags") || "";
    const [searchInput, setSearchInput] = useState(query);
    const needsScrollRef = useRef(false);

    useEffect(() => {
        setSearchInput(query);
    }, [query]);

    useEffect(() => {
        setLoading(true);
        setMessage("");
        axios
            .get(`${API_BASE_URL}/api/articles`, { params: { page, q: query || undefined, tags: activeTag || undefined, limit: 12 } })
            .then((response) => {
                const data = response.data || {};
                if (Array.isArray(data)) {
                    setArticles(data);
                    setMeta({ page: 1, pages: 1, total: data.length });
                    return;
                }
                setArticles(data.items || []);
                setMeta({
                    page: data.page || page,
                    pages: data.pages || 1,
                    total: data.total || 0,
                });
            })
            .catch(() => {
                setArticles([]);
                setMessage("Не удалось загрузить статьи.");
            })
            .finally(() => setLoading(false));
    }, [page, query, activeTag]);

    const updateParams = (nextPage, nextQuery = query, nextTag = activeTag) => {
        const params = {};
        if (nextQuery.trim()) params.q = nextQuery.trim();
        if (nextTag.trim()) params.tags = nextTag.trim();
        if (nextPage > 1) params.page = String(nextPage);
        setSearchParams(params);
        needsScrollRef.current = true;
    };

    useEffect(() => {
        if (needsScrollRef.current) {
            needsScrollRef.current = false;
            requestAnimationFrame(() => {
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            });
        }
    }, [articles]);

    const onSearchSubmit = (event) => {
        event.preventDefault();
        updateParams(1, searchInput);
    };

    const toggleLike = async (article) => {
        if (!isAuthenticated) {
            window.dispatchEvent(new CustomEvent("auth:open-login-modal", { detail: { nextPath: `/blog?${searchParams.toString()}` } }));
            return;
        }

        const wasLiked = Boolean(article.userLiked);
        setArticles((items) => items.map((item) => (
            item.id === article.id
                ? { ...item, userLiked: !wasLiked, likesCount: Math.max(0, Number(item.likesCount || 0) + (wasLiked ? -1 : 1)) }
                : item
        )));

        try {
            if (wasLiked) {
                await axios.delete(`${API_BASE_URL}/api/likes`, { data: { entityType: "blog", entityId: Number(article.id) }, withCredentials: true });
            } else {
                await axios.post(`${API_BASE_URL}/api/likes`, { entityType: "blog", entityId: Number(article.id) }, { withCredentials: true });
            }
        } catch {
            setArticles((items) => items.map((item) => (
                item.id === article.id
                    ? { ...item, userLiked: wasLiked, likesCount: Math.max(0, Number(item.likesCount || 0) + (wasLiked ? 1 : -1)) }
                    : item
            )));
            setMessage("Не удалось изменить лайк.");
        }
    };

    return (
        <div className="blog-page-root">
            <div className="blog-page-inner">
                <div className="blog-page-header">
                    <div className="blog-head">
                        <h2 className="blog-page-title">Статьи</h2>
                        <form className="blog-search" onSubmit={onSearchSubmit}>
                            <input
                                type="search"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Поиск"
                            />
                            <button type="submit" className="blog-search__button" aria-label="Найти">
                                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                    <path
                                        d="M10.5 4a6.5 6.5 0 0 1 5.17 10.44l4.45 4.44-1.24 1.24-4.44-4.45A6.5 6.5 0 1 1 10.5 4Zm0 1.75a4.75 4.75 0 1 0 0 9.5 4.75 4.75 0 0 0 0-9.5Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </button>
                        </form>
                    </div>
                    {!!activeTag && (
                        <div className="blog-filter-row">
                            <span className="blog-filter-pill">Тег: #{activeTag}</span>
                            <button
                                className="btn-outline"
                                type="button"
                                onClick={() => updateParams(1, query, "")}
                            >
                                Сбросить
                            </button>
                        </div>
                    )}
                </div>

                {!!message && <p className="cms-page-loading">{message}</p>}
                {loading && <p className="cms-page-loading">Загрузка...</p>}
                {!loading && articles.length === 0 && <p className="cms-page-loading">Публикаций пока нет.</p>}

                <div className="blog-page-grid">
                    {articles.map((article) => (
                        <article
                            className={`blog-glass-card${article.image ? "" : " blog-glass-card--no-image"}`}
                            key={article.id}
                        >
                            {!!article.image && (
                                <Link className="blog-glass-card__cover" to={`/blog/${article.slug}`}>
                                    <img
                                        src={resolveMediaUrl(article.image)}
                                        alt=""
                                        width={400}
                                        height={220}
                                        decoding="async"
                                    />
                                </Link>
                            )}
                            <div className="blog-glass-card__body">
                                <div className="blog-meta">
                                    <span>{article.createdAt ? new Date(article.createdAt).toLocaleDateString("ru-RU", { year: "numeric", month: "short", day: "numeric" }) : ""}</span>
                                    {!!article.author?.name && <span>{article.author.name}</span>}
                                </div>
                                <h3 className="blog-glass-card__title">
                                    <Link to={`/blog/${article.slug}`}>{article.title}</Link>
                                </h3>
                                {!!article.description && (
                                    <p className="blog-glass-card__desc">{article.description}</p>
                                )}
                                {!!(article.tags || []).length && (
                                    <div className="blog-tags">
                                        {article.tags.slice(0, 3).map((tag) => (
                                            <Link key={tag} to={`/blog?tags=${encodeURIComponent(tag)}`}>#{tag}</Link>
                                        ))}
                                    </div>
                                )}
                                <div className="blog-glass-card__actions">
                                    {isAuthenticated ? (
                                        <button
                                            type="button"
                                            className={article.userLiked ? "btn-liked" : "btn-like"}
                                            onClick={() => toggleLike(article)}
                                            aria-label={article.userLiked ? "Убрать лайк" : "Поставить лайк"}
                                        >
                                            <span aria-hidden style={{ fontSize: "1rem", lineHeight: 1, opacity: article.userLiked ? 1 : 0.45 }}>♥</span>
                                            <span>{article.likesCount ?? 0}</span>
                                        </button>
                                    ) : (
                                        <span className="blog-glass-card__likes">
                                            <span aria-hidden style={{ opacity: 0.4 }}>♥</span>
                                            <span>{article.likesCount ?? 0}</span>
                                        </span>
                                    )}
                                    <Link to={`/blog/${article.slug}`} className="btn-outline blog-glass-card__read">
                                        Читать
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <Pagination currentPage={meta.page} totalPages={meta.pages} onPageChange={(nextPage) => updateParams(nextPage)} />
                {!loading && meta.total > 0 && (
                    <p className="cms-page-loading">Найдено: {meta.total}</p>
                )}
            </div>
        </div>
    );
}
